import {Fragment, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {SegmentedControl} from '@/components/segmented-control'
import {Skeleton} from '@/components/ui/skeleton'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {
  groupNotifications,
  useDeleteNotification,
  useDeleteReadNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMarkNotificationsRead,
  useNotificationNavigate,
  useNotificationsPage,
  type Notification,
  type NotificationGroup,
} from '@/hooks/producto/notificaciones/use-notifications'
import {NOTIFICATION_KIND_OPTIONS} from '@/lib/notification-kinds'
import {translateApiError} from '@/lib/errors'
import {formatDate} from '@/lib/risk'
import {cn} from '@/lib/utils'

const SEVERITY_TONE: Record<string, string> = {
  info: 'bg-secondary text-muted-foreground',
  warn: 'tone-amber',
  urgent: 'tone-red',
}

function PuntoEstado({unread}: {unread: boolean}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'mt-1.5 h-2 w-2 shrink-0 rounded-full',
        unread ? 'bg-primary' : 'border border-input bg-transparent'
      )}
    />
  )
}

function NotificationDetail({detail}: {detail: string}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <div className={cn(open ? 'mb-1' : undefined)}>
      <button
        type="button"
        data-testid="notification-detail-toggle"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase hover:text-foreground">
        {t(open ? 'notifications.detail.hide' : 'notifications.detail.show')}
      </button>
      {open && (
        <pre
          data-testid="notification-detail-body"
          className="mt-1.5 max-h-48 overflow-auto rounded-md bg-secondary/60 p-2.5 font-mono text-[11px] whitespace-pre-wrap text-muted-foreground">
          {detail}
        </pre>
      )}
    </div>
  )
}

function NotificationRow({
  notice,
  onOpen,
  onDelete,
  indented,
}: {
  notice: Notification
  onOpen: (notice: Notification) => void
  onDelete: (notice: Notification) => void
  indented?: boolean
}) {
  const {t} = useTranslation()
  const unread = notice.read_at === null
  const toneClass = SEVERITY_TONE[notice.severity] ?? SEVERITY_TONE.info
  return (
    <div
      className={cn('transition-colors hover:bg-secondary/50', indented ? 'pr-5 pl-11' : 'px-5')}
      data-testid="notification-row"
      data-kind={notice.kind}
      data-unread={unread}>
      <div className="flex min-h-11 items-start gap-1 py-3.5">
        <button
          type="button"
          data-testid="notification-row-open"
          onClick={() => onOpen(notice)}
          className="flex min-w-0 flex-1 items-start gap-3 text-left">
          <PuntoEstado unread={unread} />
          <span className="min-w-0 flex-1">
            <span
              className={cn(
                'block text-sm leading-snug break-words',
                unread ? 'font-medium text-foreground' : 'text-muted-foreground'
              )}>
              {notice.message}
            </span>
            <span className="mt-1.5 flex flex-wrap items-center gap-2">
              <span
                data-testid="notification-severity"
                className={cn('rounded px-1.5 py-0.5 font-mono text-[9px] tracking-wider uppercase', toneClass)}>
                {t(`notif.severity.${notice.severity}`)}
              </span>
              <span
                data-testid="notification-read-state"
                className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                {unread ? t('notifications.unreadLabel') : t('notifications.readLabel')}
              </span>
            </span>
          </span>
        </button>
        <Button
          variant="ghost"
          size="sm"
          data-testid="notification-delete"
          onClick={(e) => {
            e.stopPropagation()
            onDelete(notice)
          }}>
          {t('notifications.deleteOne')}
        </Button>
      </div>
      {notice.detail && <NotificationDetail detail={notice.detail} />}
    </div>
  )
}

function NotificationGroupRow({
  group,
  onOpen,
  onOpenGroup,
  onDelete,
}: {
  group: NotificationGroup
  onOpen: (notice: Notification) => void
  onOpenGroup: (group: NotificationGroup) => void
  onDelete: (notice: Notification) => void
}) {
  const {t} = useTranslation()
  const [expanded, setExpanded] = useState(false)
  if (group.items.length === 1) return <NotificationRow notice={group.items[0]} onOpen={onOpen} onDelete={onDelete} />

  const head = group.items[0]
  const unreadCount = group.items.filter((n) => n.read_at === null).length
  const toneClass = SEVERITY_TONE[head.severity] ?? SEVERITY_TONE.info
  return (
    <div data-testid="notification-group" data-kind={group.kind} data-group-count={group.items.length}>
      <button
        type="button"
        data-testid="notification-group-header"
        onClick={() => onOpenGroup(group)}
        className="flex min-h-11 w-full items-start gap-3 px-5 py-3.5 text-left transition-colors hover:bg-secondary/50">
        <PuntoEstado unread={unreadCount > 0} />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block text-sm leading-snug break-words',
              unreadCount > 0 ? 'font-medium text-foreground' : 'text-muted-foreground'
            )}>
            {head.message}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-2">
            <Tag tone="neutral" data-testid="notification-group-count">
              {t('notifications.groupCount', {count: group.items.length})}
            </Tag>
            <span
              data-testid="notification-severity"
              className={cn('rounded px-1.5 py-0.5 font-mono text-[9px] tracking-wider uppercase', toneClass)}>
              {t(`notif.severity.${head.severity}`)}
            </span>
            <span
              role="button"
              aria-label={t(expanded ? 'notifications.groupCollapse' : 'notifications.groupExpand')}
              data-testid="notification-group-toggle"
              onClick={(e) => {
                e.stopPropagation()
                setExpanded((v) => !v)
              }}
              className="rounded font-mono text-[10px] tracking-wider text-muted-foreground uppercase underline-offset-2 hover:text-foreground hover:underline">
              {t(expanded ? 'notifications.groupCollapse' : 'notifications.groupExpand')}
            </span>
          </span>
        </span>
      </button>
      {expanded && (
        <div data-testid="notification-group-items" className="divide-y divide-border/60 border-t border-border/60">
          {group.items.map((notice) => (
            <NotificationRow key={notice.id} notice={notice} onOpen={onOpen} onDelete={onDelete} indented />
          ))}
        </div>
      )}
    </div>
  )
}

export function NotificationsPage() {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const [kindFilter, setKindFilter] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [markAllError, setMarkAllError] = useState<string | null>(null)
  const [deleteReadOpen, setDeleteReadOpen] = useState(false)
  const [deleteReadError, setDeleteReadError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Notification | null>(null)
  const [deleteOneError, setDeleteOneError] = useState<string | null>(null)
  const {data, isLoading} = useNotificationsPage(page, {
    kind: kindFilter ? (kindFilter as Notification['kind']) : undefined,
    unreadOnly,
  })
  const markRead = useMarkNotificationRead()
  const markGroupRead = useMarkNotificationsRead()
  const markAllRead = useMarkAllNotificationsRead()
  const deleteNotification = useDeleteNotification()
  const deleteRead = useDeleteReadNotifications()
  const goTo = useNotificationNavigate()

  const notices = data?.items ?? []
  const hasUnread = notices.some((n) => n.read_at === null)
  const hasRead = notices.some((n) => n.read_at !== null)
  const groups = groupNotifications(notices)
  const filtered = kindFilter !== '' || unreadOnly

  function openNotice(notice: Notification) {
    if (notice.read_at === null) markRead.mutate(notice.id)
    void goTo(notice)
  }

  // Abrir un grupo lo marca leído entero: el mismo contrato «abrir = enterarse» de una fila.
  function openGroup(group: NotificationGroup) {
    const unread = group.items.filter((notice) => notice.read_at === null).map((notice) => notice.id)
    if (unread.length > 0) markGroupRead.mutate(unread)
    void goTo(group.items[0])
  }

  function requestDelete(notice: Notification) {
    setDeleteOneError(null)
    setPendingDelete(notice)
  }

  function confirmDelete() {
    if (!pendingDelete) return
    deleteNotification.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
      onError: (err) => setDeleteOneError(translateApiError(err)),
    })
  }

  function confirmDeleteRead() {
    setDeleteReadError(null)
    deleteRead.mutate(undefined, {
      onSuccess: () => setDeleteReadOpen(false),
      onError: (err) => setDeleteReadError(translateApiError(err)),
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeading title={t('notifications.title')} />
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {hasRead && (
              <Button
                variant="outline"
                size="sm"
                data-testid="delete-read"
                onClick={() => {
                  setDeleteReadError(null)
                  setDeleteReadOpen(true)
                }}>
                {t('notifications.deleteRead')}
              </Button>
            )}
            {hasUnread && (
              <Button
                variant="outline"
                size="sm"
                data-testid="mark-all-read"
                disabled={markAllRead.isPending}
                onClick={() => {
                  setMarkAllError(null)
                  markAllRead.mutate(undefined, {onError: (err) => setMarkAllError(translateApiError(err))})
                }}>
                {t('notifications.markAllRead')}
              </Button>
            )}
          </div>
          {markAllError && (
            <p className="text-xs text-destructive" data-testid="mark-all-read-error">
              {markAllError}
            </p>
          )}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <ListCard
          testId="notifications-list"
          toolbar={
            <>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="font-mono text-[10px] tracking-wider uppercase">
                  {t('notifications.filters.kindLabel')}
                </span>
                <select
                  data-testid="notification-filter-kind"
                  value={kindFilter}
                  onChange={(e) => {
                    setKindFilter(e.target.value)
                    setPage(1)
                  }}
                  className="h-8 rounded-md border border-input bg-card px-2 text-xs">
                  <option value="">{t('notifications.filters.allKinds')}</option>
                  {NOTIFICATION_KIND_OPTIONS.map((kind) => (
                    <option key={kind} value={kind}>
                      {t(`notif.kindLabel.${kind}`)}
                    </option>
                  ))}
                </select>
              </label>
              <SegmentedControl
                size="sm"
                testId="notification-filter-estado"
                ariaLabel={t('notifications.filters.estadoAria')}
                value={unreadOnly ? 'noLeidas' : 'todas'}
                onChange={(key) => {
                  setUnreadOnly(key === 'noLeidas')
                  setPage(1)
                }}
                options={[
                  {key: 'todas', label: t('notifications.filters.allStates')},
                  {key: 'noLeidas', label: t('notifications.filters.unreadOnly'), testId: 'notification-filter-unread'},
                ]}
              />
            </>
          }
          footer={
            data && data.total > data.page_size ? (
              <PaginationBar page={data.page} pageSize={data.page_size} total={data.total} onPageChange={setPage} />
            ) : undefined
          }>
          {notices.length === 0 ? (
            <ListEmptyState title={filtered ? t('notifications.noMatches') : t('notifications.empty')} />
          ) : (
            // Devuelve el respiro lateral que ListCard reserva para tablas: estas filas van de canto a canto.
            <div className="-mx-2 -mt-1 divide-y divide-border">
              {/* Fecha del registro, nunca «hace N días» con el reloj del navegador: el reloj es el backend. */}
              {groups.map((group, i) => {
                const dia = formatDate(group.items[0].created_at)
                const abreDia = i === 0 || dia !== formatDate(groups[i - 1].items[0].created_at)
                return (
                  <Fragment key={group.key}>
                    {abreDia && (
                      <p
                        data-testid="notification-dia"
                        className="bg-secondary/40 px-5 py-1.5 font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                        {dia}
                      </p>
                    )}
                    <NotificationGroupRow
                      group={group}
                      onOpen={openNotice}
                      onOpenGroup={openGroup}
                      onDelete={requestDelete}
                    />
                  </Fragment>
                )
              })}
            </div>
          )}
        </ListCard>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
        title={t('notifications.deleteOneTitle')}
        description={t('notifications.deleteOneBody')}
        confirmLabel={t('notifications.deleteOne')}
        cancelLabel={t('common.cancel')}
        pending={deleteNotification.isPending}
        onConfirm={confirmDelete}
      />
      {deleteOneError && (
        <p className="text-xs text-destructive" data-testid="delete-one-error">
          {deleteOneError}
        </p>
      )}

      <ConfirmDialog
        open={deleteReadOpen}
        onOpenChange={setDeleteReadOpen}
        title={t('notifications.deleteReadTitle')}
        description={t('notifications.deleteReadBody')}
        confirmLabel={t('notifications.deleteRead')}
        cancelLabel={t('common.cancel')}
        pending={deleteRead.isPending}
        onConfirm={confirmDeleteRead}
      />
      {deleteReadError && (
        <p className="text-xs text-destructive" data-testid="delete-read-error">
          {deleteReadError}
        </p>
      )}
    </div>
  )
}
